import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Printer, Loader2, UserCog, Search } from 'lucide-react';
import { toast } from 'sonner';
import { openPrintWindow } from '@/lib/letterhead';

export default function AnagraficheDaCompletare() {
  const navigate = useNavigate();
  const [dati, setDati] = useState(null);
  const [loading, setLoading] = useState(true);
  const [soloImportati, setSoloImportati] = useState(true);
  const [q, setQ] = useState('');

  const fetchDati = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get('/ospiti/da-completare');
      setDati(r.data);
    } catch { toast.error('Errore caricamento anagrafiche'); }
    setLoading(false);
  }, []);

  useEffect(() => { fetchDati(); }, [fetchDati]);

  const righe = (dati?.ospiti || [])
    .filter(o => (!soloImportati || o.importato))
    .filter(o => `${o.cognome} ${o.nome}`.toLowerCase().includes(q.toLowerCase()));

  const stampa = () => {
    let html = `<table><thead><tr><th>Ospite</th><th>Stanza</th><th>Campi da completare</th></tr></thead><tbody>`;
    righe.forEach(o => {
      html += `<tr><td>${o.cognome} ${o.nome}</td><td>${o.stanza || '-'}${o.letto ? '/' + o.letto : ''}</td><td>${o.campi_mancanti.map(c => c.etichetta).join(', ')}</td></tr>`;
    });
    html += '</tbody></table>';
    openPrintWindow('Anagrafiche da completare', html, {
      subtitle: `${righe.length} ospiti · da consegnare al personale amministrativo`
    });
  };

  if (loading) return <div className="flex justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-brand" /></div>;

  return (
    <div className="space-y-5" data-testid="anagrafiche-da-completare-page">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold text-slate-800 font-[Cinzel]">Anagrafiche da completare</h1>
          <p className="text-sm text-slate-500 mt-0.5">
            {righe.length} ospiti con dati mancanti su {dati?.campi_totali} campi anagrafici
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <div className="relative">
            <Search className="w-4 h-4 absolute left-2.5 top-2.5 text-slate-400" />
            <Input value={q} onChange={e => setQ(e.target.value)} placeholder="Cerca ospite"
              className="h-9 pl-8 w-48" data-testid="anagrafiche-search" />
          </div>
          <Button size="sm" variant={soloImportati ? 'default' : 'outline'}
            className={soloImportati ? 'h-9 bg-brand text-white' : 'h-9'}
            onClick={() => setSoloImportati(s => !s)} data-testid="filtro-importati">
            {soloImportati ? 'Solo importati' : 'Tutti gli ospiti'}
          </Button>
          <Button size="sm" variant="outline" className="h-9" onClick={stampa} data-testid="anagrafiche-print">
            <Printer className="w-4 h-4 mr-1" />Stampa elenco
          </Button>
        </div>
      </div>

      <div className="space-y-2">
        {righe.length === 0 && (
          <Card><CardContent className="p-8 text-center text-sm text-slate-400">
            Nessuna anagrafica da completare con questi filtri.
          </CardContent></Card>
        )}
        {righe.map(o => (
          <Card key={o.id} className="hover:shadow-sm transition-shadow" data-testid={`anagrafica-row-${o.id}`}>
            <CardContent className="p-3 flex flex-wrap items-center gap-3">
              <div className="min-w-[180px]">
                <p className="text-sm font-semibold text-slate-800">{o.cognome} {o.nome}</p>
                <p className="text-[11px] text-slate-400">
                  {o.area ? `Area ${o.area}` : 'Area non assegnata'} · stanza {o.stanza || '-'}
                  {o.letto ? ` letto ${o.letto}` : ''}
                </p>
              </div>
              <div className="flex flex-wrap gap-1 flex-1">
                {o.campi_mancanti.map(c => (
                  <Badge key={c.campo} variant="outline" className="text-[10px] border-amber-200 bg-amber-50 text-amber-700">
                    {c.etichetta}
                  </Badge>
                ))}
              </div>
              <div className="flex items-center gap-2">
                <Badge className="bg-slate-100 text-slate-600 text-[10px]">{o.totale_mancanti} mancanti</Badge>
                <Button size="sm" className="h-8 bg-brand hover:bg-brand-dark text-white"
                  onClick={() => navigate(`/ospiti/${o.id}`)} data-testid={`completa-btn-${o.id}`}>
                  <UserCog className="w-3.5 h-3.5 mr-1" />Completa
                </Button>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
}
